import React, { useEffect } from 'react';
import { Table } from 'semantic-ui-react';
import orderby from 'lodash.orderby';
import { TaxMapReportOrder } from '../types';
import produce from 'immer';
import { Link } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import { FaStar } from 'react-icons/fa6';

type Direction = 'ascending' | 'descending' | null;
type ColumnNames = keyof TaxMapReportOrder;

interface State {
  column: string;
  data: TaxMapReportOrder[];
  direction: Direction;
}

function sortReducer(state: State, action: { type: string; column?: ColumnNames; data?: TaxMapReportOrder[] }) {
  switch (action.type) {
    case 'CHANGE_SORT':
      return produce(state, draft => {
        draft.column = action.column;
        draft.data = orderby(
          draft.data,
          [
            td =>
              action.column === 'orderDate'
                ? new Date(td[action.column]).valueOf()
                : td[action.column]?.toString().toLowerCase() || '',
          ],
          [draft.direction === 'ascending' && draft.column === action.column ? 'desc' : 'asc']
        );
        draft.direction =
          draft.direction === 'ascending' && draft.column === action.column ? 'descending' : 'ascending';
      });
    case 'RESET_STATE':
      return { column: null, data: action.data, direction: 'ascending' } as State;
  }
}

interface PropTypes {
  reportData: TaxMapReportOrder[];
}

const SearchViewTableBody = (props: PropTypes) => {
  const [state, dispatch] = React.useReducer(sortReducer, {
    column: null,
    data: props.reportData,
    direction: 'ascending',
  });

  useEffect(() => {
    dispatch({ type: 'RESET_STATE', data: props.reportData });
  }, [JSON.stringify(props.reportData)]);

  const sortHeader = (label: string, column: ColumnNames) => (
    <Table.HeaderCell
      sorted={state.column === column ? state.direction : null}
      onClick={() => dispatch({ type: 'CHANGE_SORT', column })}
    >
      {label}
    </Table.HeaderCell>
  );

  return (
    <div style={{ overflow: 'auto' }}>
      <Table.Header>
        <Table.Row>
          {sortHeader('Client', 'clientName')}
          {sortHeader('Title', 'titleNumber')}
          {sortHeader('HDS', 'hdsNumber')}
          {sortHeader('Date Ordered', 'orderDate')}
          <Table.HeaderCell>Searches</Table.HeaderCell>
          <Table.HeaderCell>Location</Table.HeaderCell>
          <Table.HeaderCell>County</Table.HeaderCell>
          <Table.HeaderCell>Address</Table.HeaderCell>
          <Table.HeaderCell>Municipality</Table.HeaderCell>
          <Table.HeaderCell>SubMunicipality</Table.HeaderCell>
          <Table.HeaderCell>Section</Table.HeaderCell>
          <Table.HeaderCell>Block</Table.HeaderCell>
          <Table.HeaderCell>Lot</Table.HeaderCell>
          <Table.HeaderCell>Priority</Table.HeaderCell>
          <Table.HeaderCell>Assignee</Table.HeaderCell>
          {sortHeader('Status', 'statuses')}
          {sortHeader('Tax Map Rule', 'ruleTexts')}
          <Table.HeaderCell>Tax Waiting For</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {state.data.map(rd => {
          const numberOfProperties = rd.properties.length;
          const firstProperty = rd.properties[0];
          const search =
            rd.properties.length === 1 && firstProperty.searches.length === 1 ? firstProperty.searches[0] : null;
          const allAssignees = _.uniq(rd.properties.flatMap(property => property.searches.map(item => item.assignedTo)));
          const taxSearches = _.uniq(rd.properties.flatMap(property => property.searches));
          return (
            <Table.Row key={rd.id}>
              <Table.Cell>
                {rd.isPriorityClient && <FaStar color="red" />}
                {rd.clientName}
              </Table.Cell>
              <Table.Cell>{rd.titleNumber}</Table.Cell>
              <Table.Cell>
                <Link to={{ pathname: `/orders/order-information/${rd.id}` }} target="_blank">
                  {rd.hdsNumber}
                </Link>
              </Table.Cell>
              <Table.Cell>{moment(search?.dateAdded || rd.orderDate, 'YYYY-MM-DDTHH:mm:ss.SSS').format('MM/DD/YYYY')}</Table.Cell>
              <Table.Cell>{_.uniq(rd.properties.flatMap(property => property.searches.map(item => item.searchCode))).join(', ')}</Table.Cell>
              <Table.Cell>{firstProperty?.location}</Table.Cell>
              <Table.Cell>{firstProperty?.county}</Table.Cell>
              <Table.Cell>{numberOfProperties === 1 ? firstProperty.address : `${numberOfProperties} properties`}</Table.Cell>
              <Table.Cell>{numberOfProperties === 1 && firstProperty?.municipality}</Table.Cell>
              <Table.Cell>{numberOfProperties === 1 && firstProperty?.subMunicipality}</Table.Cell>
              <Table.Cell>{numberOfProperties === 1 && firstProperty?.section}</Table.Cell>
              <Table.Cell>{numberOfProperties === 1 && firstProperty?.block}</Table.Cell>
              <Table.Cell>{numberOfProperties === 1 && firstProperty?.lot}</Table.Cell>
              <Table.Cell>{rd.isSuperRush ? 'Super Rush' : rd.isRush ? 'Rush' : ''}</Table.Cell>
              <Table.Cell>{allAssignees.length === 1 ? allAssignees[0] : ''}</Table.Cell>
              <Table.Cell>{rd.statuses}</Table.Cell>
              <Table.Cell>{rd.ruleTexts}</Table.Cell>
              <Table.Cell>{taxSearches.length === 1 ? taxSearches[0].waitingFor : ''}</Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </div>
  );
};

export default SearchViewTableBody;
